import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "action" | "secondary";

/**
 * Gros CTA tactile de l'accueil (§6.3) : un `<button>` pleine surface avec
 * glyphe + titre display + sous-titre. Au repos pas d'ombre (§5.3) ; le
 * contraste vient du fond `ground` et l'élévation au survol reste discrète
 * (filet + translation), coupée en `prefers-reduced-motion`. Focus orange.
 *
 * Quand l'utiliser : les grandes entrées d'accueil (enregistrer une idée,
 * démarrer une rencontre, importer). Pour une action en ligne ordinaire,
 * utiliser `Button`. Comme c'est un bouton, `title` est du contenu (pas le
 * `title` HTML — il est exclu du type).
 */
interface ActionCardProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "title"> {
  /** Titre de la carte (gros, display). */
  title: ReactNode;
  /** Ligne secondaire optionnelle (sous le titre). */
  subtitle?: ReactNode;
  /** Glyphe optionnel en tête (Icon, point d'enregistrement…). */
  glyph?: ReactNode;
  /**
   * Rôle visuel (défaut `secondary`).
   * - `action` : contour orange + étincelle (le CTA mis en avant).
   * - `secondary` : mauve doux (entrée de structure / secondaire).
   */
  variant?: Variant;
  /** État sélectionné (usage segmented control) → aria-pressed. */
  pressed?: boolean;
}

// DS-6 §6.3 — remplace le 5ᵉ système `.action-card` par un composant sur tokens.
// `action` = contour orange (la flamme/l'action, en étincelle, jamais un aplat) ;
// `secondary` = mauve doux (la structure). L'anneau de focus est orange (§5.1).
// Au repos, pas d'ombre (§5.3) : le contraste vient du fond `ground` ; l'élévation
// au survol reste discrète (filet + translation), coupée en reduced-motion.
const VARIANT: Record<Variant, string> = {
  action:
    "border border-orange-deep bg-orange-soft text-orange-deep hover:border-orange active:bg-orange-soft",
  secondary:
    "border border-flambeau-200 bg-flambeau-50 text-flambeau-700 hover:bg-flambeau-100 hover:border-flambeau-300 active:bg-flambeau-200",
};

export function ActionCard({
  title,
  subtitle,
  glyph,
  variant = "secondary",
  pressed,
  className = "",
  type = "button",
  ...rest
}: ActionCardProps) {
  const classes = [
    "flex min-h-[96px] flex-col gap-2 rounded-lg p-4 text-left",
    "transition-[transform,border-color,background-color] duration-150 motion-reduce:transition-none",
    "active:scale-[0.98] motion-reduce:active:scale-100",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-2",
    "disabled:cursor-not-allowed disabled:opacity-55",
    VARIANT[variant],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button type={type} className={classes} aria-pressed={pressed} {...rest}>
      {glyph && <span className="flex items-center justify-between gap-2">{glyph}</span>}
      <span className="mt-auto flex flex-col gap-0.5">
        <span className="font-display text-title-sm font-bold leading-tight tracking-tight">
          {title}
        </span>
        {subtitle && <span className="text-small font-normal opacity-80">{subtitle}</span>}
      </span>
    </button>
  );
}
